/**
 * Карта 3.0 — страница.
 *
 * Собственный движок (frontend/src/mapv3), без React Flow и Cytoscape.
 * React здесь отвечает только за панели вокруг холста; сам холст императивный,
 * поэтому перетаскивание не вызывает перерисовку компонентов.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, IconButton, Tooltip, Button, Chip, Stack, Divider,
  TextField, InputAdornment, Menu, MenuItem, ListItemIcon, ListItemText,
  CircularProgress, Alert, Snackbar, ToggleButton, ToggleButtonGroup,
  FormControl, InputLabel, Select, OutlinedInput, Checkbox, Drawer,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import GridOnIcon from '@mui/icons-material/GridOn';
import DownloadIcon from '@mui/icons-material/Download';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import TimelineIcon from '@mui/icons-material/Timeline';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import ImageIcon from '@mui/icons-material/Image';
import HideImageIcon from '@mui/icons-material/HideImage';
import UploadIcon from '@mui/icons-material/Upload';

import { buildScene } from '../mapv3/scene';
import { createEngine } from '../mapv3/engine';
import { autoLayout } from '../mapv3/layout';
import * as mapApi from '../mapv3/api';
import { exportSVG, exportPNG, exportCablesCSV } from '../mapv3/export';

const HINT = 'ЛКМ — выделить · тянуть — двигать · Alt+ЛКМ или колесо — панорама · '
  + 'выдели линию и потяни её в любом месте — согнётся · двойной клик по рыжей точке — убрать изгиб · '
  + 'тяни белый кружок на корпусе — переставить порт · Ctrl+Z — отменить';

export default function MapV3() {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const engineRef = useRef(null);
  const sceneRef = useRef(null);
  const stateRef = useRef({ sceneId: 1, docVersion: 1 });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [stats, setStats] = useState({ devices: 0, edges: 0, locs: 0 });
  const [status, setStatus] = useState({ routed: 0, approx: 0, ms: 0 });
  const [sel, setSel] = useState([]);
  const [hist, setHist] = useState({ canUndo: false, canRedo: false });
  const [zoom, setZoom] = useState(1);
  const [menu, setMenu] = useState(null);
  const [saving, setSaving] = useState('');

  const [theme, setTheme] = useState(() => localStorage.getItem('mapv3.theme') || 'light');
  const [grid, setGrid] = useState(() => localStorage.getItem('mapv3.grid') !== '0');
  const [labels, setLabels] = useState(() => localStorage.getItem('mapv3.labels') || 'type');
  const [showPorts, setShowPorts] = useState(() => localStorage.getItem('mapv3.ports') !== '0');
  const [images, setImages] = useState(() => localStorage.getItem('mapv3.images') !== '0');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);

  // ------------------------------------------------------------- загрузка

  /**
   * Сохранение с защитой от наложения запросов.
   *
   * Без неё легко попасть в тупик: два сохранения накладываются, второе уходит
   * со старой версией документа, получает 409 — и дальше КАЖДОЕ сохранение
   * возвращает 409 до перезагрузки страницы, а пользователь продолжает работать,
   * не зная, что его правки больше никуда не уезжают.
   * Поэтому: пока запрос в полёте, следующий кладём в очередь; на 409 сначала
   * пробуем подтянуть свежую версию и повторить, и только если и это не вышло —
   * честно говорим, что дальше сохранять некуда.
   */
  const inFlight = useRef(false);
  const queued = useRef(null);

  const save = useCallback(async (items, meta, remove = []) => {
    const payload = { items: [...items, meta], remove };
    if (inFlight.current) { queued.current = payload; return; }
    inFlight.current = true;
    try {
      setSaving('Сохранение…');
      let res;
      try {
        res = await mapApi.saveItems(
          stateRef.current.sceneId, stateRef.current.docVersion, payload.items, payload.remove,
        );
      } catch (e) {
        if (e?.response?.status !== 409) throw e;
        // версия разъехалась — берём актуальную и пробуем ещё раз, один раз
        const fresh = await mapApi.loadState(stateRef.current.sceneId);
        stateRef.current.docVersion = fresh.scene.doc_version;
        res = await mapApi.saveItems(
          stateRef.current.sceneId, stateRef.current.docVersion, payload.items, payload.remove,
        );
      }
      stateRef.current.docVersion = res.doc_version;
      setSaving('Сохранено');
      setTimeout(() => setSaving((v) => (v === 'Сохранено' ? '' : v)), 1400);
    } catch (e) {
      setSaving('не сохранено');
      if (e?.response?.status === 409) {
        setError('Карту одновременно меняет кто-то ещё. Обновите страницу, чтобы увидеть её версию, — иначе ваши правки не сохранятся.');
      } else if (e?.response?.status === 403) {
        setError('Нет права на изменение карты (map.edit) — раскладка не сохраняется.');
      } else if (e?.response?.status === 401) {
        setError('Сессия истекла — войдите заново, иначе правки потеряются.');
      } else {
        setError('Не удалось сохранить раскладку. Проверьте связь с сервером.');
      }
    } finally {
      inFlight.current = false;
      const next = queued.current;
      queued.current = null;
      if (next) save(next.items.slice(0, -1), next.items[next.items.length - 1], next.remove);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await mapApi.loadState();
        if (!alive) return;

        stateRef.current = { sceneId: data.scene.id, docVersion: data.scene.doc_version };
        const scene = buildScene(data.topology, data.items);
        sceneRef.current = scene;

        // ничего не расставлено — раскладываем сами, чтобы не встречать пустым листом
        const anyPlaced = [...scene.nodes.values()].some((n) => n.placed);
        if (!anyPlaced) autoLayout(scene, { force: true });

        setStats({ devices: scene.nodes.size, edges: scene.edges.size, locs: scene.conts.size });
        if (scene.dropped.length) setToast(`Пропущено устаревших записей: ${scene.dropped.length}`);

        const eng = createEngine(canvasRef.current, overlayRef.current, scene, {
          theme, grid, labels, showPorts, images, onSave: save,
        });
        engineRef.current = eng;

        eng.on('selection', setSel);
        eng.on('history', (h) => setHist(h));
        eng.on('status', setStatus);
        eng.on('viewport', (v) => setZoom(v.scale));
        eng.on('toast', setToast);
        eng.on('menu', (m) => setMenu(m));
        eng.on('open', ({ id }) => navigate(`/devices/${id}`));

        if (!anyPlaced) eng.commit('initial');
        eng.reroute();   // подписки уже висят — панель получит счётчик трасс
        eng.fit();
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.status === 403
          ? 'Нет права на просмотр карты (map.view)'
          : 'Не удалось загрузить карту');
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
      engineRef.current?.dispose();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onResize = () => engineRef.current?.resize();
    window.addEventListener('resize', onResize);
    const ro = wrapRef.current && 'ResizeObserver' in window
      ? new ResizeObserver(onResize) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    return () => { window.removeEventListener('resize', onResize); ro?.disconnect(); };
  }, []);

  // ------------------------------------------------------ настройки вида

  const setOpt = (key, value, storeKey) => {
    engineRef.current?.setOption(key, value);
    if (storeKey) localStorage.setItem(storeKey, typeof value === 'boolean' ? (value ? '1' : '0') : value);
  };

  useEffect(() => { setOpt('theme', theme, 'mapv3.theme'); }, [theme]);
  useEffect(() => { setOpt('grid', grid, 'mapv3.grid'); }, [grid]);
  useEffect(() => { setOpt('labels', labels, 'mapv3.labels'); }, [labels]);
  useEffect(() => { setOpt('showPorts', showPorts, 'mapv3.ports'); }, [showPorts]);
  useEffect(() => { setOpt('images', images, 'mapv3.images'); }, [images]);

  // --------------------------------------------------------- поиск/фильтр

  const connTypes = useMemo(() => {
    const s = sceneRef.current;
    if (!s) return [];
    const seen = new Map();
    for (const e of s.edges.values()) {
      if (!seen.has(e.typeId)) seen.set(e.typeId, { id: e.typeId, name: e.label || 'Связь', color: e.color });
    }
    return [...seen.values()];
  }, [loading]);

  useEffect(() => {
    const eng = engineRef.current, s = sceneRef.current;
    if (!eng || !s) return;
    const q = query.trim().toLowerCase();
    if (!q && !typeFilter.length) { eng.setFilter(null); return; }
    const keep = new Set();
    for (const n of s.nodes.values()) {
      const hit = !q || n.name.toLowerCase().includes(q) ||
        (n.ip || '').includes(q) || (n.typeName || '').toLowerCase().includes(q);
      if (hit) keep.add(n.id);
    }
    for (const e of s.edges.values()) {
      const typeOk = !typeFilter.length || typeFilter.includes(e.typeId);
      const nodeOk = keep.has(e.a) || keep.has(e.b);
      if (typeOk && (!q || nodeOk)) keep.add(e.id);
    }
    eng.setFilter(keep);
  }, [query, typeFilter]);

  const runSearch = () => {
    const s = sceneRef.current, eng = engineRef.current;
    if (!s || !eng) return;
    const q = query.trim().toLowerCase();
    if (!q) return;
    const found = [...s.nodes.values()].find(
      (n) => n.name.toLowerCase().includes(q) || (n.ip || '').includes(q),
    );
    if (found) eng.focusNode(found.id);
    else setToast('Ничего не нашлось');
  };

  // ------------------------------------------------------- действия меню

  const menuAction = (fn) => () => { setMenu(null); fn(); };

  const doExport = async (kind) => {
    const s = sceneRef.current, eng = engineRef.current;
    if (!s || !eng) return;
    try {
      if (kind === 'svg') exportSVG(s, eng.ui);
      else if (kind === 'png') { await exportPNG(s, eng.ui); }
      else exportCablesCSV(s);
      setToast('Файл выгружен');
    } catch (e) { setError('Не удалось выгрузить файл'); }
  };

  const fileRef = useRef(null);

  /** Картинка грузится тем же эндпоинтом, что и в карточке устройства,
   *  поэтому она сразу появляется и там, и здесь. */
  const onPickFile = async (ev) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    const id = sel[0]?.id;
    if (!file || !id) return;
    if (!/^image\//.test(file.type)) { setError('Это не картинка'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Картинка больше 5 МБ — уменьшите её'); return; }
    try {
      setToast('Загружаю картинку…');
      const res = await mapApi.uploadDeviceImage(id, file);
      const path = res?.image_path || res?.path || null;
      engineRef.current?.setNodeImage(id, path);
      setToast('Картинка загружена');
    } catch (e) {
      setError(e?.response?.status === 403
        ? 'Нет права на изменение устройств'
        : 'Не удалось загрузить картинку');
    }
  };

  const dropImage = async (id) => {
    try {
      await mapApi.deleteDeviceImage(id);
      engineRef.current?.setNodeImage(id, null);
      setToast('Картинка убрана');
    } catch (e) { setError('Не удалось убрать картинку'); }
  };

  const selInfo = sel[0];

  // ---------------------------------------------------------------- вид

  const dark = theme === 'dark';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 96px)', minHeight: 520, gap: 1 }}>
      {/* ---- панель инструментов ---- */}
      <Paper elevation={0} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>Карта 3.0</Typography>

          <Chip size="small" label={`Устройств: ${stats.devices}`} />
          <Chip size="small" color="primary" variant="outlined" label={`Связей: ${stats.edges}`} />
          <Chip size="small" variant="outlined" label={`Локаций: ${stats.locs}`} />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Расставить всё заново">
            <span><Button size="small" startIcon={<AutoFixHighIcon />}
              onClick={() => engineRef.current?.autoArrange()}>Разложить</Button></span>
          </Tooltip>
          <Tooltip title="Убрать все ручные изгибы и вернуть автоматические трассы">
            <span><Button size="small" startIcon={<LinearScaleIcon />}
              onClick={() => { engineRef.current?.resetRoutes(); setToast('Трассы пересчитаны заново'); }}>
              Трассы заново</Button></span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Отменить (Ctrl+Z)"><span>
            <IconButton size="small" disabled={!hist.canUndo} onClick={() => engineRef.current?.undo()}>
              <UndoIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="Повторить (Ctrl+Shift+Z)"><span>
            <IconButton size="small" disabled={!hist.canRedo} onClick={() => engineRef.current?.redo()}>
              <RedoIcon fontSize="small" /></IconButton></span></Tooltip>

          <Tooltip title="Приблизить"><IconButton size="small" onClick={() => engineRef.current?.zoomBy(1.25)}>
            <ZoomInIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Отдалить"><IconButton size="small" onClick={() => engineRef.current?.zoomBy(0.8)}>
            <ZoomOutIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Показать всё (F)"><IconButton size="small" onClick={() => engineRef.current?.fit()}>
            <CenterFocusStrongIcon fontSize="small" /></IconButton></Tooltip>
          <Chip size="small" variant="outlined" label={`${Math.round(zoom * 100)}%`} sx={{ minWidth: 62 }} />

          <Box sx={{ flex: 1 }} />

          <TextField
            size="small" placeholder="Найти устройство или IP" value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
            sx={{ width: 240 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Типы связей</InputLabel>
            <Select
              multiple value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              input={<OutlinedInput label="Типы связей" />}
              renderValue={(v) => (v.length ? `Выбрано: ${v.length}` : 'Все')}
            >
              {connTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Checkbox size="small" checked={typeFilter.includes(t.id)} />
                  <Box sx={{ width: 14, height: 3, bgcolor: t.color, mr: 1, borderRadius: 1 }} />
                  <ListItemText primary={t.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup size="small" exclusive value={labels} onChange={(e, v) => v && setLabels(v)}>
            <ToggleButton value="type">Тип</ToggleButton>
            <ToggleButton value="iface">Порты</ToggleButton>
            <ToggleButton value="subnet">Сеть</ToggleButton>
            <ToggleButton value="none">Без</ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title={images ? 'Картинки устройств включены' : 'Картинки выключены — рисуем схематичные значки'}>
            <IconButton size="small" color={images ? 'primary' : 'default'} onClick={() => setImages((v) => !v)}>
              {images ? <ImageIcon fontSize="small" /> : <HideImageIcon fontSize="small" />}</IconButton></Tooltip>
          <Tooltip title="Точки портов на корпусах">
            <IconButton size="small" color={showPorts ? 'primary' : 'default'} onClick={() => setShowPorts((v) => !v)}>
              <SettingsEthernetIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Сетка (G)">
            <IconButton size="small" color={grid ? 'primary' : 'default'} onClick={() => setGrid((v) => !v)}>
              <GridOnIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title={dark ? 'Светлая тема' : 'Тёмная тема'}>
            <IconButton size="small" onClick={() => setTheme(dark ? 'light' : 'dark')}>
              {dark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}</IconButton></Tooltip>

          <Tooltip title="Выгрузить">
            <IconButton size="small" onClick={(e) => setMenu({ export: true, x: e.clientX, y: e.clientY })}>
              <DownloadIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Перечитать данные">
            <IconButton size="small" onClick={() => window.location.reload()}>
              <RefreshIcon fontSize="small" /></IconButton></Tooltip>
        </Stack>
      </Paper>

      {/* ---- холст ---- */}
      <Box ref={wrapRef} sx={{ position: 'relative', flex: 1, minHeight: 380, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />
        <canvas ref={overlayRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }} />

        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,0.6)' }}>
            <Stack alignItems="center" spacing={1}><CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">Собираем схему…</Typography></Stack>
          </Box>
        )}

        {/* подсказка */}
        <Paper elevation={0} sx={{
          position: 'absolute', left: 10, bottom: 10, px: 1.2, py: 0.6, maxWidth: 620,
          bgcolor: dark ? 'rgba(15,20,27,0.86)' : 'rgba(255,255,255,0.9)',
          border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
        }}>
          <Typography variant="caption" color="text.secondary">{HINT}</Typography>
        </Paper>

        {/* легенда */}
        {!!connTypes.length && (
          <Paper elevation={0} sx={{
            position: 'absolute', right: 10, bottom: 10, px: 1.2, py: 0.8,
            bgcolor: dark ? 'rgba(15,20,27,0.86)' : 'rgba(255,255,255,0.9)',
            border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
          }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Типы связей</Typography>
            {connTypes.map((t) => (
              <Stack key={t.id} direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.4 }}>
                <Box sx={{ width: 20, height: 3, bgcolor: t.color, borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">{t.name}</Typography>
              </Stack>
            ))}
          </Paper>
        )}

        {/* состояние трассировки */}
        <Paper elevation={0} sx={{
          position: 'absolute', right: 10, top: 10, px: 1.2, py: 0.5,
          bgcolor: dark ? 'rgba(15,20,27,0.86)' : 'rgba(255,255,255,0.9)',
          border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
        }}>
          <Typography variant="caption" color="text.secondary">
            Трасс: {status.routed} · {status.ms} мс{saving ? ` · ${saving}` : ''}
          </Typography>
          {status.approx > 0 && (
            <Typography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
              приблизительных: {status.approx} — там схема не сошлась
            </Typography>
          )}
        </Paper>
      </Box>

      {/* ---- инспектор ---- */}
      {!!selInfo && (
        <Paper elevation={0} sx={{ p: 1.2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {selInfo.type === 'node' && (<>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{selInfo.ent.name}</Typography>
              <Chip size="small" variant="outlined" label={selInfo.ent.typeName || 'тип не задан'} />
              {selInfo.ent.ip && <Chip size="small" variant="outlined" label={selInfo.ent.ip} />}
              <Chip size="small" variant="outlined" label={`связей: ${selInfo.ent.degree || 0}`} />
              <Button size="small" startIcon={<UploadIcon />} onClick={() => fileRef.current?.click()}>
                {selInfo.ent.imagePath ? 'Заменить картинку' : 'Загрузить картинку'}</Button>
              {selInfo.ent.imagePath && (
                <Button size="small" color="inherit" onClick={() => dropImage(selInfo.id)}>Убрать картинку</Button>
              )}
              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate(`/devices/${selInfo.id}`)}>
                Открыть карточку</Button>
            </>)}
            {selInfo.type === 'edge' && (<>
              <Box sx={{ width: 22, height: 4, bgcolor: selInfo.ent.color, borderRadius: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {sceneRef.current?.nodes.get(selInfo.ent.a)?.name} → {sceneRef.current?.nodes.get(selInfo.ent.b)?.name}
              </Typography>
              <Chip size="small" variant="outlined" label={selInfo.ent.label || 'связь'} />
              {selInfo.ent.aIface && <Chip size="small" variant="outlined" label={`${selInfo.ent.aIface} → ${selInfo.ent.bIface || '—'}`} />}
              <Chip size="small" variant="outlined" label={`изгибов: ${(selInfo.ent.vias || []).length}`} />
              <Button size="small" startIcon={<TimelineIcon />}
                onClick={() => { engineRef.current?.straighten([selInfo.id]); setToast('Линия выпрямлена'); }}>
                Выпрямить</Button>
            </>)}
            {selInfo.type === 'cont' && (<>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{selInfo.ent.name}</Typography>
              <Chip size="small" variant="outlined" label={`${Math.round(selInfo.ent.w)}×${Math.round(selInfo.ent.h)}`} />
              <Button size="small" startIcon={<AutoFixHighIcon />}
                onClick={() => { engineRef.current?.arrangeInside(selInfo.id); setToast('Содержимое локации прибрано'); }}>
                Прибрать внутри</Button>
            </>)}
            {sel.length > 1 && <Chip size="small" color="primary" label={`выделено: ${sel.length}`} />}
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={() => engineRef.current?.selectIds([])}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        </Paper>
      )}

      {/* ---- контекстное меню ---- */}
      <Menu
        open={!!menu} onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      >
        {menu?.export ? [
          <MenuItem key="svg" onClick={menuAction(() => doExport('svg'))}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Схема в SVG (для печати)</ListItemText></MenuItem>,
          <MenuItem key="png" onClick={menuAction(() => doExport('png'))}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Схема в PNG</ListItemText></MenuItem>,
          <MenuItem key="csv" onClick={menuAction(() => doExport('csv'))}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Журнал кабелей (CSV)</ListItemText></MenuItem>,
        ] : [
          menu?.hit?.type === 'edge' || menu?.hit?.type === 'via' || menu?.hit?.type === 'segment' ? (
            <MenuItem key="straight" onClick={menuAction(() => engineRef.current?.straighten())}>
              <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Выпрямить линию</ListItemText></MenuItem>
          ) : null,
          menu?.hit?.type === 'cont' ? (
            <MenuItem key="inside" onClick={menuAction(() => engineRef.current?.arrangeInside(menu.hit.id))}>
              <ListItemIcon><AutoFixHighIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Прибрать внутри локации</ListItemText></MenuItem>
          ) : null,
          menu?.hit?.type === 'node' ? (
            <MenuItem key="open" onClick={menuAction(() => navigate(`/devices/${menu.hit.id}`))}>
              <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Открыть карточку устройства</ListItemText></MenuItem>
          ) : null,
          <MenuItem key="auto" onClick={menuAction(() => engineRef.current?.autoArrange())}>
            <ListItemIcon><AutoFixHighIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Разложить всю схему</ListItemText></MenuItem>,
        ].filter(Boolean)}
      </Menu>

      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} />

      <Snackbar open={!!toast} autoHideDuration={2600} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      <Snackbar open={!!error} autoHideDuration={7000} onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}
